import type { ApiError } from '@huddle/shared';

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Every call to the Worker goes through here.
 *
 * `credentials: 'same-origin'` is the important bit — the session is an
 * HTTP-only cookie, so there is no token for the app to hold, forward, or leak
 * into localStorage.
 */
export async function api<T>(
  path: string,
  options: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...init } = options;

  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  });

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiError | null;
    throw new ApiFailure(
      response.status,
      body?.error ?? 'error',
      body?.message ?? 'Something went wrong.',
    );
  }

  return (await response.json()) as T;
}

export const get = <T>(path: string) => api<T>(path);
export const post = <T>(path: string, json?: unknown) => api<T>(path, { method: 'POST', json });
export const patch = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PATCH', json });
export const put = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PUT', json });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/** True when the failure means "not signed in", so callers can redirect. */
export function isAuthFailure(error: unknown): boolean {
  return error instanceof ApiFailure && error.status === 401;
}
